/**
 * MCG IPTV Uploader Specifications
 * 
 * Single source of truth for all MCG IPTV placements,
 * dimensions, and validation specs.
 * 
 * Placements include:
 * - IPTV Suite (grouped: Full, Side, LWrap, JWrap, Strap)
 * - General IPTV Content
 * - Function Rooms (Landscape & Portrait)
 * - Changeroom LED
 * - External Gates & Totems
 * - Players Race (Home & Away)
 */

// ============================================================
// IPTV PLACEMENTS (PILLS)
// ============================================================
export const IPTV_PLACEMENTS = [
  { label: "IPTV Suite", placementId: "IPTV-SUITE" },
  { label: "General IPTV Content", placementId: "GENERAL-AA" },
  { label: "Function Rooms", placementId: "FUNC-RMS" },
  { label: "Function Rooms (Portrait)", placementId: "FUNC-RMSP" },
  { label: "Changeroom LED", placementId: "CHANGE-RM-LED" },
  { label: "External Gates", placementId: "EXT-GATES" },
  { label: "External Totems", placementId: "EXT-TOTEMS" },
  { label: "Players Race Home", placementId: "PLAYERS-RACE-HOME" },
  { label: "Players Race Away", placementId: "PLAYERS-RACE-AWAY" },
];

// ============================================================
// PLACEMENT → UPLOADER GROUPS
// ============================================================
export const IPTV_PLACEMENT_GROUPS = {
  "IPTV-SUITE": ["MCG-SUITE-FULL", "MCG-SUITE-SIDE", "MCG-SUITE-LWRAP", "MCG-SUITE-JWRAP", "MCG-SUITE-STRAP"],
  "GENERAL-AA": ["GENERAL-AA"],
  "FUNC-RMS": ["FUNC-RMS"],
  "FUNC-RMSP": ["FUNC-RMSP"],
  "CHANGE-RM-LED": ["CHANGE-RM-LED"],
  "EXT-GATES": ["EXT-GATES"],
  "EXT-TOTEMS": ["EXT-TOTEMS"],
  "PLAYERS-RACE-HOME": ["PR-HOME-TOP", "PR-HOME-LEFT", "PR-HOME-RIGHT"],
  "PLAYERS-RACE-AWAY": ["PR-AWAY-TOP", "PR-AWAY-SIDE1", "PR-AWAY-SIDE2"],
};

// ============================================================
// GROUPED FOLDER PLACEMENTS
// Only these placements get SHORTNAME_TIME folders
// ============================================================
export const GROUPED_FOLDER_PLACEMENTS = new Set([
  "IPTV-SUITE",
  "EXT-GATES",
  "PLAYERS-RACE-HOME",
  "PLAYERS-RACE-AWAY",
]);

// ============================================================
// ASSET NAME PLACEMENTS
// These placements require a user-entered Asset Name (like LED Tiled/360)
// ============================================================
export const ASSET_NAME_PLACEMENTS = new Set(["IPTV-SUITE", "PLAYERS-RACE-HOME", "PLAYERS-RACE-AWAY"]);

// ============================================================
// COMMON SPECS
// ============================================================
const IPTV_VIDEO_COMMON = {
  allowed: true,
  extensions: ["mp4"],
  formats: ["AVC"],
  frameRate: 25,
  duration: null,
  audio: { mode: "optional" },
};

const IPTV_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png"],
};

// ============================================================
// SPEC FACTORIES
// ============================================================
function singleUploaderSpec({ label, specId, prefix, width, height }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "singleAsset",
    requiredFiles: 1,
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB per file
    dimensions: [{ width, height }],
    videoSpec: { ...IPTV_VIDEO_COMMON },
    imageSpec: { ...IPTV_IMAGE_COMMON },
  };
}

function groupedUploaderSpec({ label, specId, prefix, width, height, groupFolder, frameRate }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "groupedAssets",
    groupFolder,
    requiredFiles: 1,
    maxFiles: 1,
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB per file
    dimensions: [{ width, height }],
    videoSpec: { ...IPTV_VIDEO_COMMON, ...(frameRate != null && { frameRate }) },
    imageSpec: { ...IPTV_IMAGE_COMMON },
  };
}

function suiteUploaderSpec({ label, specId, prefix, requiresAlpha = true }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "groupedAssets",
    groupFolder: "SUITE", // Overridden at render time to include assetName: SUITE_{assetName}
    requiredFiles: 1,
    maxFiles: 1,
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB per file
    dimensions: [{ width: 1920, height: 1080 }],
    videoSpec: {
      ...IPTV_VIDEO_COMMON,
      duration: 15, // 15 second requirement
    },
    imageSpec: {
      ...IPTV_IMAGE_COMMON,
      requiresAlpha,
      inspectAlpha: requiresAlpha,
    },
  };
}

// ============================================================
// UPLOADER SPECS (SINGLE SOURCE OF TRUTH)
// ============================================================
export const UPLOADER_SPECS = {
  // ── IPTV Suite (5 grouped buckets) ──────────────────────────
  "MCG-SUITE-FULL": suiteUploaderSpec({
    label: "Full",
    specId: "MCG-SUITE-FULL",
    prefix: "FULL",
    requiresAlpha: false, // Full does NOT require alpha channel
  }),

  "MCG-SUITE-SIDE": suiteUploaderSpec({
    label: "Side",
    specId: "MCG-SUITE-SIDE",
    prefix: "SIDE",
  }),

  "MCG-SUITE-LWRAP": suiteUploaderSpec({
    label: "LWrap",
    specId: "MCG-SUITE-LWRAP",
    prefix: "LWRAP",
  }),

  "MCG-SUITE-JWRAP": suiteUploaderSpec({
    label: "JWrap",
    specId: "MCG-SUITE-JWRAP",
    prefix: "JWRAP",
  }),

  "MCG-SUITE-STRAP": suiteUploaderSpec({
    label: "Strap",
    specId: "MCG-SUITE-STRAP",
    prefix: "STRAP",
  }),

  // ── Single placements ──────────────────────────────────────
  "GENERAL-AA": singleUploaderSpec({
    label: "General IPTV Content",
    specId: "GENERAL-AA",
    prefix: "GAA",
    width: 1920,
    height: 1080,
  }),

  "FUNC-RMS": singleUploaderSpec({
    label: "Function Rooms",
    specId: "FUNC-RMS",
    prefix: "FR",
    width: 1920,
    height: 1080,
  }),

  "FUNC-RMSP": singleUploaderSpec({
    label: "Function Rooms (Portrait)",
    specId: "FUNC-RMSP",
    prefix: "FRP",
    width: 1080,
    height: 1920,
  }),

  "CHANGE-RM-LED": singleUploaderSpec({
    label: "Changeroom LED",
    specId: "CHANGE-RM-LED",
    prefix: "CRLED",
    width: 1440,
    height: 384,
  }),

  "EXT-GATES": {
    ...groupedUploaderSpec({
      label: "External Gates",
      specId: "EXT-GATES",
      prefix: "EXG",
      groupFolder: "EXT-GATES",
      frameRate: 30,
      width: 1920,
      height: 1080,
    }),
    maxFiles: Infinity,
  },

  "EXT-TOTEMS": singleUploaderSpec({
    label: "External Totems",
    specId: "EXT-TOTEMS",
    prefix: "EXT",
    width: 1080,
    height: 1920,
  }),

  // Players Races — HOME (all panels share one PR-HOME folder, like LED tiled)
  "PR-HOME-TOP": groupedUploaderSpec({
    label: "Players Race Home - Top Panel",
    specId: "PR-HOME-TOP",
    prefix: "PRH-TOP",
    groupFolder: "PR-HOME",
    frameRate: 60,
    width: 1920,
    height: 1080,
  }),

  "PR-HOME-LEFT": groupedUploaderSpec({
    label: "Players Race Home - Left Panel",
    specId: "PR-HOME-LEFT",
    prefix: "PRH-LEFT",
    groupFolder: "PR-HOME",
    frameRate: 60,
    width: 3840,
    height: 2160,
  }),

  "PR-HOME-RIGHT": groupedUploaderSpec({
    label: "Players Race Home - Right Panel",
    specId: "PR-HOME-RIGHT",
    prefix: "PRH-RIGHT",
    groupFolder: "PR-HOME",
    frameRate: 60,
    width: 3840,
    height: 2160,
  }),

  // Players Races — AWAY (all panels share one PR-AWAY folder, like LED tiled)
  "PR-AWAY-TOP": groupedUploaderSpec({
    label: "Players Race Away - Top Panel",
    specId: "PR-AWAY-TOP",
    prefix: "PRA-TOP",
    groupFolder: "PR-AWAY",
    frameRate: 60,
    width: 360,
    height: 200,
  }),

  "PR-AWAY-SIDE1": groupedUploaderSpec({
    label: "Players Race Away - Side 1",
    specId: "PR-AWAY-SIDE1",
    prefix: "PRA-S1",
    groupFolder: "PR-AWAY",
    frameRate: 60,
    width: 3840,
    height: 2160,
  }),

  "PR-AWAY-SIDE2": groupedUploaderSpec({
    label: "Players Race Away - Side 2",
    specId: "PR-AWAY-SIDE2",
    prefix: "PRA-S2",
    groupFolder: "PR-AWAY",
    frameRate: 60,
    width: 3840,
    height: 2160,
  }),
};

// ============================================================
// HELPER: Build human-readable spec label
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.dimensions) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  }

  // Video
  if (spec.videoSpec) {
    parts.push("MP4");
    parts.push("H.264");
    parts.push(`${spec.videoSpec.frameRate}fps`);
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    if (Array.isArray(spec.imageSpec.extensions) && spec.imageSpec.extensions.length) {
      parts.push(
        `Static ${spec.imageSpec.extensions.map((e) => e.toUpperCase()).join(" / ")}`
      );
    } else {
      parts.push("Static Image");
    }
    parts.push(spec.imageSpec.requiresAlpha ? "RGBA" : "RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// HELPER: Check if Players Race panel
// ============================================================
export function isPlayersRacePanel(uploadId, spec) {
  return (
    spec?.contentGroup === "groupedAssets" &&
    (uploadId.startsWith("PR-HOME-") || uploadId.startsWith("PR-AWAY-"))
  );
}

// ============================================================
// METADATA WEBHOOK (Club submissions - not commercial)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";

const mcgIptvSpecs = {
  IPTV_PLACEMENTS,
  IPTV_PLACEMENT_GROUPS,
  GROUPED_FOLDER_PLACEMENTS,
  ASSET_NAME_PLACEMENTS,
  UPLOADER_SPECS,
  buildSpecLabel,
  isPlayersRacePanel,
  METADATA_WEBHOOK,
};

export default mcgIptvSpecs;
